import React from 'react';
import { Wind, Clock, HeartPulse } from 'lucide-react';
export function NerveDischarge() {
  return <section className="py-20 lg:py-32 px-6 bg-charcoal text-cream relative overflow-hidden" data-id="element-92">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-93"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-94">
        <div className="text-center mb-16" data-id="element-95">
          <span className="text-gold font-serif italic text-xl mb-4 block" data-id="element-96">
            Chapter 3
          </span>
          <h2 className="text-4xl lg:text-6xl font-serif text-cream mb-6" data-id="element-97">
            The 2-Minute Nerve Discharge
          </h2>
          <p className="text-lg text-cream/70 max-w-2xl mx-auto" data-id="element-98">
            The core mechanism of the blueprint. Perform this twice daily: once
            upon waking and once before your largest meal.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-99">
          {/* The Technique Card */}
          <div className="bg-charcoal-light p-8 lg:p-12 rounded-sm border border-white/10" data-id="element-100">
            <h3 className="text-2xl font-serif text-gold mb-8 flex items-center gap-3" data-id="element-101">
              <Wind className="w-6 h-6" data-id="element-102" />
              The "Physiological Sigh"
            </h3>

            <div className="space-y-8 relative" data-id="element-103">
              {/* Connecting line */}
              <div className="absolute left-[15px] top-4 bottom-4 w-px bg-white/10" data-id="element-104"></div>

              <div className="flex gap-6 relative" data-id="element-105">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sage flex items-center justify-center text-white font-serif font-bold text-sm z-10" data-id="element-106">
                  1
                </div>
                <div data-id="element-107">
                  <h4 className="text-lg font-medium text-white mb-1" data-id="element-108">
                    Deep Inhale
                  </h4>
                  <p className="text-cream/60 text-sm" data-id="element-109">
                    Inhale deeply through the nose until lungs are full.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 relative" data-id="element-110">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sage flex items-center justify-center text-white font-serif font-bold text-sm z-10" data-id="element-111">
                  2
                </div>
                <div data-id="element-112">
                  <h4 className="text-lg font-medium text-white mb-1" data-id="element-113">
                    Second "Pop" Inhale
                  </h4>
                  <p className="text-cream/60 text-sm" data-id="element-114">
                    Take a second, shorter "sharp" inhale to pop open the tiny
                    sacs in the lungs.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 relative" data-id="element-115">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sage flex items-center justify-center text-white font-serif font-bold text-sm z-10" data-id="element-116">
                  3
                </div>
                <div data-id="element-117">
                  <h4 className="text-lg font-medium text-white mb-1" data-id="element-118">
                    Slow Exhale
                  </h4>
                  <p className="text-cream/60 text-sm" data-id="element-119">
                    Exhale through the mouth very slowly (twice as long as the
                    inhale).
                  </p>
                </div>
              </div>

              <div className="flex gap-6 relative" data-id="element-120">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold flex items-center justify-center text-charcoal font-serif font-bold text-sm z-10" data-id="element-121">
                  4
                </div>
                <div data-id="element-122">
                  <h4 className="text-lg font-medium text-gold mb-1" data-id="element-123">
                    Repeat for 2 Minutes
                  </h4>
                  <p className="text-cream/60 text-sm" data-id="element-124">
                    This timing is critical for the nervous system reset.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why It Works */}
          <div className="space-y-8" data-id="element-125">
            <div className="flex gap-4 items-start" data-id="element-126">
              <div className="p-3 bg-white/5 rounded-full" data-id="element-127">
                <HeartPulse className="w-6 h-6 text-gold" data-id="element-128" />
              </div>
              <div data-id="element-129">
                <h3 className="text-xl font-serif text-white mb-2" data-id="element-130">
                  Vagus Nerve Activation
                </h3>
                <p className="text-cream/60 leading-relaxed" data-id="element-131">
                  This is the only physical hack that manually forces the Vagus
                  nerve to tell the brain to lower heart rate and cortisol
                  levels instantly.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start" data-id="element-132">
              <div className="p-3 bg-white/5 rounded-full" data-id="element-133">
                <Clock className="w-6 h-6 text-gold" data-id="element-134" />
              </div>
              <div data-id="element-135">
                <h3 className="text-xl font-serif text-white mb-2" data-id="element-136">
                  Immediate Effect
                </h3>
                <p className="text-cream/60 leading-relaxed" data-id="element-137">
                  Unlike meditation which can take 20+ minutes to show
                  physiological changes, the Physiological Sigh works within
                  30-60 seconds.
                </p>
              </div>
            </div>

            <div className="p-6 bg-sage/20 border border-sage/30 rounded-sm mt-8" data-id="element-138">
              <p className="text-center text-sage-light italic font-serif" data-id="element-139">
                "It's like hitting Ctrl+Alt+Delete for your nervous system."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}